import { ChevronDown, Check } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, parseApiDate } from "@/lib/utils";

export interface VersionedItem {
  _id?: string;
  created_on?: string | Date;
}

interface Props {
  /** Versions in any order — this component sorts descending by created_on. */
  versions: VersionedItem[];
  selectedId: string;
  onChange: (id: string) => void;
}

const relative = (d?: string | Date) => {
  if (!d) return "unknown";
  try {
    return formatDistanceToNow(parseApiDate(d), { addSuffix: true }).replace(
      /^about /,
      "",
    );
  } catch {
    return "unknown";
  }
};

/**
 * Compact `v{n} · {relative date}` dropdown. Hidden when there's only a single
 * version (callers can still render it; this component simply renders null).
 *
 * Newest entry is labeled `v{versions.length}` and appears first.
 */
export function VersionSelector({ versions, selectedId, onChange }: Props) {
  if (!versions || versions.length < 2) return null;

  const sorted = [...versions]
    .filter((v) => v._id)
    .sort((a, b) => {
      const ad = a.created_on ? new Date(a.created_on).getTime() : 0;
      const bd = b.created_on ? new Date(b.created_on).getTime() : 0;
      return bd - ad;
    });
  const count = sorted.length;

  const selectedIndex = sorted.findIndex((v) => v._id === selectedId);
  const active =
    selectedIndex >= 0 ? sorted[selectedIndex] : sorted[0];
  const activeNumber =
    selectedIndex >= 0 ? count - selectedIndex : count;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
          title="Pick report version"
        >
          <span className="font-mono">v{activeNumber}</span>
          <span className="hidden text-muted-foreground/60 sm:inline">
            · {relative(active.created_on)}
          </span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        {sorted.map((v, idx) => {
          const num = count - idx;
          const isActive = v._id === selectedId;
          return (
            <DropdownMenuItem
              key={v._id}
              onClick={() => v._id && onChange(v._id)}
              className={cn(
                "flex items-center gap-2",
                isActive && "bg-accent/50"
              )}
            >
              <Check
                className={cn(
                  "h-3.5 w-3.5 shrink-0",
                  isActive ? "opacity-100" : "opacity-0"
                )}
              />
              <span className="font-mono text-xs">v{num}</span>
              <span className="flex-1 truncate text-xs text-muted-foreground">
                {relative(v.created_on)}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
